/**
 * Influencers Dashboard — list of influencers with promo-code count and users joined.
 * Click a row to open the editable detail page.
 */
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress } from '@mui/joy'
import { Campaign, Search as SearchIcon, Visibility, Close, People, LocalOffer, CheckCircle, Add, DeleteOutline, ContentCopy, Check } from '@mui/icons-material'
import { motion } from 'framer-motion'
import { billingApi } from './api'
import { useInfluencers } from './InfluencersContext'
import { InfluencerDetail, InfluencerProfile } from './types'
import InfluencerCreateModal from './InfluencerCreateModal'
import ConfirmDeleteModal from './ConfirmDeleteModal'

const ease = [0.23, 1, 0.32, 1]

function getInitials(name: string): string {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
}

function getAvatarColor(name: string): string {
    const colors = [
        'bg-blue-100 text-blue-700',
        'bg-violet-100 text-violet-700',
        'bg-emerald-100 text-emerald-700',
        'bg-amber-100 text-amber-700',
        'bg-rose-100 text-rose-700',
        'bg-cyan-100 text-cyan-700',
        'bg-indigo-100 text-indigo-700',
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
    return colors[Math.abs(hash) % colors.length]
}

const tierBadge = (tier: string) => {
    const styles: Record<string, string> = {
        Silver: 'bg-gray-100 text-gray-600',
        Gold: 'bg-amber-100 text-amber-700',
        Platinum: 'bg-violet-100 text-violet-700',
    }
    return <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${styles[tier] || styles.Silver}`}>{tier}</span>
}

export default function InfluencersPage() {
    const navigate = useNavigate()
    const { influencers, loading, error, ensureLoaded, applyCreate, applyDelete } = useInfluencers()
    const [search, setSearch] = useState('')
    const [createOpen, setCreateOpen] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<InfluencerDetail | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [copiedId, setCopiedId] = useState<string | null>(null)

    const handleCopyLink = async (link: string | null, id: string) => {
        if (!link) return
        try {
            await navigator.clipboard.writeText(link)
            setCopiedId(id)
            setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000)
        } catch {
            // Clipboard unavailable (e.g. insecure context) — no-op.
        }
    }

    useEffect(() => {
        ensureLoaded()
    }, [ensureLoaded])

    const handleCreated = (profile: InfluencerProfile) => {
        applyCreate({
            ...profile,
            lastLogin: null,
            createdAt: Date.now(),
            promoCodeCount: 0,
            usersJoined: 0,
            totalRedemptions: 0,
            promoCodes: [],
            referralLinkSignups: 0,
            referralConversions: 0,
            commissionEarnedCents: 0,
        })
    }

    const handleDelete = async () => {
        if (!deleteTarget) return
        try {
            setDeleting(true)
            setDeleteError(null)
            await billingApi.deleteInfluencer(deleteTarget.id)
            applyDelete(deleteTarget.id)
            setDeleteTarget(null)
        } catch (err: any) {
            setDeleteError(err.response?.data?.message || 'Failed to delete influencer')
        } finally {
            setDeleting(false)
        }
    }

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return influencers
        return influencers.filter((inf) => inf.name.toLowerCase().includes(q) || (inf.email || '').toLowerCase().includes(q))
    }, [influencers, search])

    const totals = useMemo(
        () => ({
            count: influencers.length,
            promoCodes: influencers.reduce((s, i) => s + i.promoCodeCount, 0),
            usersJoined: influencers.reduce((s, i) => s + i.usersJoined, 0),
            portalEnabled: influencers.filter((i) => i.isPortalEnabled).length,
        }),
        [influencers]
    )

    const STAT_CARDS = [
        { label: 'Influencers', value: totals.count, color: 'border-t-indigo-500', iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', Icon: Campaign },
        { label: 'Promo Codes', value: totals.promoCodes, color: 'border-t-blue-500', iconBg: 'bg-blue-50', iconColor: 'text-blue-600', Icon: LocalOffer },
        {
            label: 'Users Joined',
            value: totals.usersJoined,
            color: 'border-t-emerald-500',
            iconBg: 'bg-emerald-50',
            iconColor: 'text-emerald-600',
            Icon: People,
        },
        {
            label: 'Portal Enabled',
            value: totals.portalEnabled,
            color: 'border-t-violet-500',
            iconBg: 'bg-violet-50',
            iconColor: 'text-violet-600',
            Icon: CheckCircle,
        },
    ]

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
                <span>Home</span>
                <span>/</span>
                <span>Billing</span>
                <span>/</span>
                <span className="text-gray-600 font-medium">Influencers</span>
            </div>

            {/* Page Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="flex items-center gap-3 mb-6"
            >
                <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Campaign sx={{ fontSize: 18 }} className="text-indigo-600" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Influencers</h1>
                    <p className="text-sm text-gray-500">Affiliate partners, their promo codes and referred signups</p>
                </div>
                <button
                    onClick={() => setCreateOpen(true)}
                    className="ml-auto h-9 px-4 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
                >
                    <Add sx={{ fontSize: 16 }} />
                    New Influencer
                </button>
            </motion.div>

            {/* Stat Cards */}
            <div className="grid grid-cols-4 gap-3 mb-6">
                {STAT_CARDS.map((stat, i) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.05, ease }}
                    >
                        <div
                            className={`bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200 border-t-[3px] ${stat.color} p-4`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">{stat.label}</p>
                                <div className={`w-7 h-7 rounded-md ${stat.iconBg} flex items-center justify-center`}>
                                    <stat.Icon sx={{ fontSize: 14 }} className={stat.iconColor} />
                                </div>
                            </div>
                            {loading ? <CircularProgress size="sm" /> : <p className="text-2xl font-bold text-gray-900">{stat.value}</p>}
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Table */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    {/* Header */}
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <Campaign sx={{ fontSize: 16 }} className="text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900">All Influencers</p>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{filtered.length}</span>
                    </div>

                    {/* Search */}
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex gap-3 items-end">
                        <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Search by Name or Email</label>
                            <div className="relative">
                                <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    placeholder="Search influencers..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full h-9 text-sm pl-8 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                        </div>
                        {search && (
                            <button onClick={() => setSearch('')} className="h-9 px-3 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1">
                                <Close sx={{ fontSize: 13 }} />
                                Clear
                            </button>
                        )}
                    </div>

                    {error && <div className="mx-5 mt-3 text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700">{error}</div>}

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50/80 text-left">
                                    <th className="px-5 py-3 text-xs font-semibold text-gray-600 w-[52px]"></th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Name</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Email</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Tier</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right">Users Joined</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right">Promo Codes</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right pr-5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="text-center py-12">
                                            <CircularProgress size="sm" />
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                                            No influencers found
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((inf, idx) => (
                                        <motion.tr
                                            key={inf.id}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2, delay: idx * 0.03, ease }}
                                            className="hover:bg-indigo-50/40 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0 group"
                                            onClick={() => navigate(`/billing/influencers/${inf.id}`)}
                                        >
                                            <td className="py-2.5 pl-5 pr-0">
                                                <div
                                                    className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${getAvatarColor(inf.name)}`}
                                                >
                                                    {getInitials(inf.name)}
                                                </div>
                                            </td>
                                            <td className="py-2.5 px-2">
                                                <span className="font-medium text-gray-800 group-hover:text-indigo-700 transition-colors">{inf.name}</span>
                                            </td>
                                            <td className="py-2.5 px-2 text-gray-600 truncate max-w-[220px]">
                                                {inf.email || <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="py-2.5 px-2">{tierBadge(inf.tier)}</td>
                                            <td className="py-2.5 px-2 text-right font-semibold text-gray-800">{inf.usersJoined}</td>
                                            <td className="py-2.5 px-2 text-right text-gray-600">{inf.promoCodeCount}</td>
                                            <td className="py-2.5 px-2 text-right pr-5" onClick={(e) => e.stopPropagation()}>
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        onClick={() => handleCopyLink(inf.referralLink, inf.id)}
                                                        disabled={!inf.referralLink}
                                                        title="Copy referral link"
                                                        className="w-7 h-7 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center disabled:opacity-30"
                                                    >
                                                        {copiedId === inf.id ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/billing/influencers/${inf.id}`)}
                                                        className="w-7 h-7 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center"
                                                    >
                                                        <Visibility sx={{ fontSize: 16 }} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setDeleteError(null)
                                                            setDeleteTarget(inf)
                                                        }}
                                                        className="w-7 h-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center"
                                                    >
                                                        <DeleteOutline sx={{ fontSize: 16 }} />
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>

            <InfluencerCreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />
            <ConfirmDeleteModal
                open={!!deleteTarget}
                title="Delete influencer"
                message={
                    <span>
                        Delete <strong>{deleteTarget?.name}</strong>?
                        {!!deleteTarget?.promoCodeCount && (
                            <>
                                {' '}
                                Their {deleteTarget.promoCodeCount} promo code{deleteTarget.promoCodeCount > 1 ? 's' : ''} will remain in Stripe but become
                                unlinked.
                            </>
                        )}
                        {deleteError && <div className="mt-2 font-medium">{deleteError}</div>}
                    </span>
                }
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    )
}
